import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAppId } from '../../context/AppIdContext';
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const VideoHelp = () => {
    const navigate = useNavigate();
    const { language, appId } = useAppId();
    
    const lang = language || 'en';

    const [targetUrl, setTargetUrl] = useState("");
    const [loading, setLoading] = useState(true);

    // --- Translation Dictionary ---
    const t = {
        en: {
            title: "Video Help",
            loadingText: "Loading Video Help Portal...",
            errorText: "Failed to load the portal. Please try again."
        },
        hi: {
            title: "वीडियो सहायता",
            loadingText: "वीडियो सहायता पोर्टल लोड हो रहा है...",
            errorText: "पोर्टल लोड करने में विफल। कृपया पुनः प्रयास करें।"
        },
        gu: {
            title: "વિડીયો હેલ્પ",
            loadingText: "વિડીયો હેલ્પ પોર્ટલ લોડ થઈ રહ્યું છે...",
            errorText: "પોર્ટલ લોડ કરવામાં નિષ્ફળ. કૃપા કરીને ફરી પ્રયાસ કરો."
        }
    };

    const txt = t[lang] || t.en;

    // --- API Fetch Logic ---
    useEffect(() => {
        if (!appId) {
            navigate("/");
            return;
        }

        const fetchAppUrl = async () => {
            setLoading(true);
            try {
                const response = await axios.post(`${BASE_URL}/api/get_app_urls`, { 
                    link_type: "video_help" 
                });

                if (response.data && response.data.success) {
                    const url = response.data.data?.url || "";
                    
                    if (url) {
                        setTargetUrl(url);
                    }
                }
            } catch (error) {
                console.error("Error fetching video help url:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchAppUrl();
    }, [appId, navigate]);

    return (
        // 🔴 FIX: Card લેઆઉટ માટે પેડિંગ પાછું ઉમેર્યું (p-3 p-md-4)
        <div className="app-container p-3 p-md-4" style={{ minHeight: '100vh', background: '#eef2f6' }}>
            
            {/* Header */}
            <Header title={txt.title} />

            {/* iFrame Container - Card Style */}
            <div className="iframe-card-wrapper slide-up-element mt-4">
                
                {loading && (
                    <div className="iframe-loading-indicator">
                        <div className="spinner"></div>
                        <span>{txt.loadingText}</span>
                    </div>
                )}

                {!loading && !targetUrl && (
                    <div className="iframe-loading-indicator text-danger">
                        <div style={{fontSize: '40px', marginBottom: '10px'}}>⚠️</div>
                        <span>{txt.errorText}</span>
                    </div>
                )}

                {!loading && targetUrl && (
                    <iframe 
                        src={targetUrl} 
                        title="Video Help Portal"
                        className="responsive-iframe custom-scrollbar-iframe"
                        allowFullScreen
                        sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                    ></iframe>
                )}
                
            </div>

            <style jsx>{`
                /* Custom Scrollbar Styles for the entire page */
                .app-container {
                    scrollbar-width: thin;
                    scrollbar-color: #6366f1 #e2e8f0;
                }

                .app-container::-webkit-scrollbar {
                    width: 8px;
                    height: 8px;
                }

                .app-container::-webkit-scrollbar-track {
                    background: #e2e8f0;
                    border-radius: 10px;
                }

                .app-container::-webkit-scrollbar-thumb {
                    background: #6366f1;
                    border-radius: 10px;
                    transition: all 0.2s ease;
                }

                .app-container::-webkit-scrollbar-thumb:hover {
                    background: #4f46e5;
                    cursor: pointer;
                }

                /* 🔴 FIX: Card Layout Styles for iframe */
                .iframe-card-wrapper {
                    position: relative;
                    width: 100%;
                    height: calc(100vh - 140px); /* Adjusted for card margins */
                    min-height: 500px;
                    background: #ffffff;
                    border-radius: 20px;
                    border: 1px solid #e2e8f0;
                    box-shadow: 0 10px 30px -5px rgba(0, 0, 0, 0.08);
                    overflow: hidden;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    scrollbar-width: thin;
                    scrollbar-color: #6366f1 #f1f5f9;
                }

                .iframe-card-wrapper::-webkit-scrollbar {
                    width: 8px;
                    height: 8px;
                }

                .iframe-card-wrapper::-webkit-scrollbar-track {
                    background: #f1f5f9;
                    border-radius: 10px;
                }

                .iframe-card-wrapper::-webkit-scrollbar-thumb {
                    background: #6366f1;
                    border-radius: 10px;
                    transition: all 0.2s ease;
                }

                .iframe-card-wrapper::-webkit-scrollbar-thumb:hover {
                    background: #4f46e5;
                    cursor: pointer;
                }

                .responsive-iframe {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    border: none;
                    z-index: 10;
                    background-color: transparent;
                    border-radius: 20px;
                }

                /* Loading Indicator behind the iframe */
                .iframe-loading-indicator {
                    position: absolute;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    color: #64748b;
                    font-weight: 600;
                    font-size: 15px;
                    z-index: 1;
                    text-align: center;
                    padding: 20px;
                }

                .spinner { 
                    width: 40px; 
                    height: 40px; 
                    border: 3px solid #e2e8f0; 
                    border-top: 3px solid #6366f1; /* Indigo color */
                    border-radius: 50%; 
                    animation: spin 1s linear infinite; 
                    margin-bottom: 12px; 
                }

                .slide-up-element { 
                    animation: slideUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; 
                    opacity: 0;
                }
                
                @keyframes slideUp { 
                    from { opacity: 0; transform: translateY(20px); } 
                    to { opacity: 1; transform: translateY(0); } 
                }
                @keyframes spin { 
                    0% { transform: rotate(0deg); } 
                    100% { transform: rotate(360deg); } 
                }

                /* Mobile Adjustments */
                @media (max-width: 768px) {
                    .app-container {
                        padding: 16px !important;
                    }
                    .iframe-card-wrapper {
                        height: calc(100vh - 120px);
                        border-radius: 16px;
                    }
                    .responsive-iframe {
                        border-radius: 16px;
                    }
                    
                    /* Smaller scrollbar on mobile */
                    .app-container::-webkit-scrollbar,
                    .iframe-card-wrapper::-webkit-scrollbar {
                        width: 4px;
                        height: 4px;
                    }
                }
            `}</style>
        </div>
    );
};

export default VideoHelp;